import type { IntlKey } from 'locales';
import { FC, ReactNode, useMemo, useRef } from 'react';
import { SidePanelTabId } from 'state/uiPanelState/sidePanelUi/sidePanelUi.model';
import styled from 'styled-components';
import { useIntersectionObserver } from 'usehooks-ts';

import { GenericErrorBoundary } from '../../builder/form/GenericErrorBoundary';
import { HeaderDropdown, HeaderDropdownItem, HeaderItem } from './HeaderItem';

interface TopTabsHeaderProps {
  summaryTabContent: ReactNode;
  hasRegulatory: boolean;

  regulatoryTabContent: ReactNode;

  regulatoryAdditionalHeaderContent?: ReactNode;

  hasAdvisory: boolean;

  advisoryTabContent: ReactNode;
  hasGreeks: boolean;
  hasFees: boolean;
  hasMetrics: boolean;
  isForBooking?: boolean;
  hasBackTest: boolean;
  greeksFeesMetricsTabContent: ReactNode;
  hasMissedDealFeedBack: boolean;
  missedDealFeedBackContent: ReactNode;
  backTestTabContent: ReactNode;
  backTestAdditionalHeaderContent?: ReactNode;

  handleChangeOfActiveTab: Nullable<(id: SidePanelTabId) => void>;
  activeTab: SidePanelTabId;
}

interface SensitivityTranslationKeys {
  key: IntlKey;
  tooltipKey?: IntlKey;
}

function getSensitivityTranslationKeys(flag: {
  hasGreeks: boolean;
  hasFees: boolean;
  hasMetrics: boolean;
}): SensitivityTranslationKeys {
  const hasOnlyGreeks = flag.hasGreeks && !flag.hasFees && !flag.hasMetrics;
  return hasOnlyGreeks
    ? { key: 'sidePanel.tabs.greeks' }
    : { key: 'sidePanel.tabs.greeksFeesMetrics', tooltipKey: 'sidePanel.tabs.greeksFeesMetrics.tooltip' };
}

const StyledHeaderContainer = styled.div`
  overflow-x: hidden;
`;
const TabsList = styled.ul`
  width: fit-content;
`;
interface TabHeader {
  id: SidePanelTabId;
  translationKey?: IntlKey;
  tooltipTranslationKey?: IntlKey;
  children?: ReactNode;
}

export const TopTabs: FC<TopTabsHeaderProps> = ({
  hasRegulatory,
  hasAdvisory,
  hasMissedDealFeedBack,
  hasGreeks,
  hasFees,
  hasBackTest,
  hasMetrics,
  isForBooking,
  backTestTabContent,
  activeTab,
  handleChangeOfActiveTab,
  regulatoryAdditionalHeaderContent,
  backTestAdditionalHeaderContent,
  summaryTabContent,
  regulatoryTabContent,
  advisoryTabContent,
  missedDealFeedBackContent,
  greeksFeesMetricsTabContent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { isIntersecting, ref: tabListRef } = useIntersectionObserver({ root: containerRef.current, threshold: 0.95 });
  const hasDropdown = !isIntersecting;

  const hasGreeksFeesMetrics = hasGreeks || hasFees || hasMetrics;
  const sensitivityTranslationKeys = getSensitivityTranslationKeys({ hasGreeks, hasFees, hasMetrics });

  const tabHeaders = useMemo<TabHeader[]>(() => {
    const headers: TabHeader[] = [{ id: 'quotation' }];

    if (hasRegulatory) {
      headers.push({ id: 'regulatory', children: regulatoryAdditionalHeaderContent });
    }
    if (!isForBooking && hasGreeksFeesMetrics) {
      headers.push({
        id: 'greeksFeesMetrics',
        translationKey: sensitivityTranslationKeys.key,
        tooltipTranslationKey: sensitivityTranslationKeys.tooltipKey,
      });
    }
    if (hasAdvisory) {
      headers.push({ id: 'advisory' });
    }
    if (hasBackTest) {
      headers.push({ id: 'backTest', children: backTestAdditionalHeaderContent });
    }
    if (hasMissedDealFeedBack) {
      headers.push({ id: 'missedDealFeedback' });
    }

    return headers;
  }, [
    isForBooking,
    hasRegulatory,
    hasGreeksFeesMetrics,
    hasAdvisory,
    hasBackTest,
    hasMissedDealFeedBack,
    regulatoryAdditionalHeaderContent,
    sensitivityTranslationKeys,
    backTestAdditionalHeaderContent,
  ]);

  const secondaryHeaders = useMemo<TabHeader[]>(() => {
    return tabHeaders.slice(2).sort((header1, _header2) => (header1.id === activeTab ? -1 : 1));
  }, [activeTab, tabHeaders]);

  const sortedHeaders = useMemo(() => tabHeaders.slice(0, 2).concat(secondaryHeaders), [secondaryHeaders, tabHeaders]);

  return (
    <GenericErrorBoundary key="poor-man-error-boundary-side-panel-tabs">
      <div className="card bg-lvl1 border-start d-flex flex-column h-100 position-relative">
        <div className="d-flex flex-row w-100 py-2">
          <StyledHeaderContainer className="d-flex align-items-center flex-grow-1" ref={containerRef}>
            <TabsList className="nav flex-nowrap gap-3 px-3" ref={tabListRef}>
              {(hasDropdown ? sortedHeaders : tabHeaders).map(
                ({ id, translationKey, tooltipTranslationKey, children }) => (
                  <HeaderItem
                    key={id}
                    id={id}
                    activeTabId={activeTab}
                    handleChangeOfActiveTab={handleChangeOfActiveTab}
                    translationKey={translationKey}
                    tooltipTranslationKey={tooltipTranslationKey}
                  >
                    {children}
                  </HeaderItem>
                ),
              )}
            </TabsList>
          </StyledHeaderContainer>
          {hasDropdown && (
            <ul className="nav mx-2">
              <HeaderDropdown>
                {tabHeaders.map(({ id, translationKey, tooltipTranslationKey }) => (
                  <HeaderDropdownItem
                    key={id}
                    id={id}
                    activeTabId={activeTab}
                    handleChangeOfActiveTab={handleChangeOfActiveTab}
                    translationKey={translationKey}
                    tooltipTranslationKey={tooltipTranslationKey}
                  />
                ))}
              </HeaderDropdown>
            </ul>
          )}
        </div>

        <div className="flex-grow-1 min-h-0 d-flex flex-column">
          {activeTab === 'quotation' && summaryTabContent}
          {activeTab === 'regulatory' && hasRegulatory && regulatoryTabContent}
          {activeTab === 'greeksFeesMetrics' && hasGreeksFeesMetrics && greeksFeesMetricsTabContent}
          {activeTab === 'advisory' && hasAdvisory && advisoryTabContent}
          {activeTab === 'backTest' && hasBackTest && backTestTabContent}
          {activeTab === 'missedDealFeedback' && hasMissedDealFeedBack && missedDealFeedBackContent}
        </div>
      </div>
    </GenericErrorBoundary>
  );
};
